import { useMemo } from "react";
import { ArrowRight, FileText, Search, ShieldCheck } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { ReferralComposer } from "@/components/ReferralComposer";
import { useCity } from "@/contexts/CityContext";
import { referralMethodLabel } from "@/lib/availability";
import { useBoardHome, useSavedFacilities } from "@/lib/board";
import { useSpecialistProfile, type Facility } from "@/lib/public-directory";
import type { ReferralTemplateInput } from "@/lib/referral-template";
import "@/styles/specialist-profile-redesign.css";

interface ReferralCandidate {
  slug: string;
  name: string;
  specialty: string;
  source: "Saved" | "Recently confirmed";
}

export default function BoardReferrals() {
  const { city } = useCity();
  const [params, setParams] = useSearchParams();
  const home = useBoardHome(city.slug);
  const saved = useSavedFacilities(city.slug);
  const candidates = useMemo<ReferralCandidate[]>(() => {
    const bySlug = new Map<string, ReferralCandidate>();
    for (const facility of saved.data?.items ?? []) {
      bySlug.set(facility.slug, {
        slug: facility.slug,
        name: facility.name,
        specialty: facility.address_json.specialty ?? "Specialist",
        source: "Saved",
      });
    }
    for (const facility of home.data?.nextPatient.recentlyConfirmed ?? []) {
      if (!bySlug.has(facility.facilitySlug)) {
        bySlug.set(facility.facilitySlug, {
          slug: facility.facilitySlug,
          name: facility.name,
          specialty: facility.specialty,
          source: "Recently confirmed",
        });
      }
    }
    return Array.from(bySlug.values());
  }, [home.data?.nextPatient.recentlyConfirmed, saved.data?.items]);
  const selectedSlug = params.get("facility") ?? candidates[0]?.slug ?? "";
  const profile = useSpecialistProfile(city.slug, selectedSlug);
  const specialist = profile.data?.specialist;
  const templateInput = specialist ? referralInput(specialist) : null;

  return (
    <div className="pb-referrals-page">
      <header className="pb-board-page-heading">
        <div>
          <p className="pb-board-eyebrow">From connection to care</p>
          <h1>Referral composer</h1>
          <p>Start from a saved or recently confirmed specialist, then finish the letter in your clinical system.</p>
        </div>
      </header>

      <div className="pb-board-referral-layout">
        <aside className="pb-board-referral-picker" aria-labelledby="referral-picker-heading">
          <h2 id="referral-picker-heading">Choose a specialist</h2>
          {home.isLoading || saved.isLoading ? <p>Loading referral options…</p> : null}
          {home.isError || saved.isError ? <p role="alert">Some referral options could not be loaded. <button type="button" onClick={() => { if (home.isError) void home.refetch(); if (saved.isError) void saved.refetch(); }}>Retry</button></p> : null}
          {!home.isLoading && !saved.isLoading && !home.isError && !saved.isError && candidates.length === 0 ? (
            <div className="pb-referral-picker-empty">
              <Search size={24} aria-hidden="true" />
              <strong>Your specialists will appear here</strong>
              <p>Save a specialist from the directory for quick access next time.</p>
              <Link to="/directory?type=specialist">Find a specialist <ArrowRight size={14} aria-hidden="true" /></Link>
            </div>
          ) : (
            <ul>
              {candidates.map((candidate) => (
                <li key={candidate.slug}>
                  <button
                    type="button"
                    aria-pressed={candidate.slug === selectedSlug}
                    onClick={() => setParams({ facility: candidate.slug }, { replace: true })}
                  >
                    <strong>{candidate.name}</strong>
                    <span>{candidate.specialty}</span>
                    <small>{candidate.source}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="pb-board-referral-work" aria-live="polite">
          {!selectedSlug ? (
            <div className="pb-referral-welcome">
              <div className="pb-workspace-emblem" aria-hidden="true"><FileText size={42} strokeWidth={1.3} /></div>
              <p className="pb-board-eyebrow">A clear next step</p>
              <h2>A considered referral starts here.</h2>
              <p>Bring the specialist’s contact and referral details together, ready for your clinical system.</p>
              <Link className="pb-board-primary" to="/directory?type=specialist">Explore the specialist directory <ArrowRight size={16} aria-hidden="true" /></Link>
              <ol className="pb-referral-steps">
                <li><span>01</span><strong>Choose a specialist</strong><p>Find the right destination for your referral.</p></li>
                <li><span>02</span><strong>Prepare your draft</strong><p>Use their practice and referral details.</p></li>
                <li><span>03</span><strong>Finish in your clinical system</strong><p>Add patient information and send from there.</p></li>
              </ol>
              <div className="pb-referral-privacy"><ShieldCheck size={17} aria-hidden="true" /> Patient details stay in your clinical system.</div>
            </div>
          ) : profile.isLoading ? (
            <div className="pb-board-state">Loading referral details…</div>
          ) : profile.isError || !specialist || !templateInput ? (
            <div className="pb-board-state pb-board-state--error" role="alert">
              Referral details could not be loaded. <button type="button" onClick={() => void profile.refetch()}>Retry</button>
            </div>
          ) : (
            <>
              <div className="pb-board-referral-selected">
                <div>
                  <span>Referral destination</span>
                  <h2>{specialist.name}</h2>
                  <p>{specialist.address_json.specialty ?? specialist.address_json.practice_name ?? "Specialist"}</p>
                </div>
                <Link to={`/directory/specialist/${specialist.slug}`}>View full profile</Link>
              </div>
              <ReferralComposer
                input={templateInput}
                facilityId={specialist.id}
                headingId="board-referral-composer-heading"
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function referralInput(facility: Facility): ReferralTemplateInput {
  const address = [
    facility.address_json.street,
    facility.address_json.suburb,
    facility.address_json.state,
    facility.address_json.postcode,
  ].filter(Boolean).join(", ");
  return {
    specialistName: facility.name,
    practiceName: facility.address_json.practice_name ?? facility.practiceGroup?.name,
    address: address || facility.address_json.full_address,
    phone: facility.phone,
    fax: facility.availability?.referralFax ?? facility.address_json.fax,
    email: facility.availability?.referralEmail ?? facility.address_json.email,
    referralMethod: facility.availability?.referralMethod
      ? referralMethodLabel(facility.availability.referralMethod)
      : null,
    referralNotes: facility.availability?.referralNotes,
  };
}
